// Release notes (ERP-68). Every merge request that changes what people see or do adds one file to
// `apps/web/src/releases/`, named `<yyyy-mm-dd>-<slug>.md`:
//
//   ---
//   title: Brokers on loans
//   tickets: ERP-64
//   modules: loans
//   ---
//   - Applications record the broker who brought the customer.
//
// Merging to main deploys, so the notes in a build are what that deploy brought. After a deploy each
// user is shown the notes they haven't seen yet, once.

export interface ReleaseNote {
  /** The file name without `.md`: date first, so ids sort by date. */
  id: string;
  date: string;
  title: string;
  tickets: string[];
  /** Module keys the note is about; empty means everyone. */
  modules: string[];
  body: string;
}

const FRONT = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const NAME = /^(\d{4}-\d{2}-\d{2})-[a-z0-9-]+$/;

export function parseRelease(path: string, raw: string): ReleaseNote {
  const id = path.replace(/^.*\//, "").replace(/\.md$/, "");
  const date = id.match(NAME)?.[1];
  if (!date) throw new Error(`release note ${path}: name it <yyyy-mm-dd>-<slug>.md`);
  const m = raw.match(FRONT);
  const meta: Record<string, string> = {};
  for (const line of (m?.[1] ?? "").split(/\r?\n/)) {
    const i = line.indexOf(":");
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  if (!meta.title) throw new Error(`release note ${path}: needs a title`);
  const list = (v?: string) =>
    (v ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return {
    id,
    date,
    title: meta.title,
    tickets: list(meta.tickets),
    modules: list(meta.modules),
    body: (m ? raw.slice(m[0].length) : raw).trim(),
  };
}

/** Newest first. */
export function releaseNotes(files: Record<string, string>): ReleaseNote[] {
  return Object.entries(files)
    .map(([p, raw]) => parseRelease(p, raw))
    .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

/**
 * What to show a user after a deploy: the notes they haven't seen, about modules they have. `seen`
 * is the ids they've already been shown — a set, not "the newest seen", so a note added later
 * for a day already read still shows. Someone with no record (a new user) gets only the latest
 * day's notes, not the whole history.
 */
export function unseenNotes(
  notes: ReleaseNote[],
  seen: string[] | null,
  modules: string[] | undefined,
): ReleaseNote[] {
  const mine = notes.filter((n) => !n.modules.length || n.modules.some((m) => (modules ?? []).includes(m)));
  if (!seen) return mine.filter((n) => n.date === mine[0]?.date);
  const read = new Set(seen);
  return mine.filter((n) => !read.has(n.id));
}
